import type {
  MergeDecisionDto,
  MergeRowDto,
  RowDecisionStatus,
} from '../api/mergeResolution.types';

import { periodKey } from './cellKey';

/**
 * Determine the decision status of a row based on its cells and user decisions.
 *
 * Rules in order:
 * 1. A CLEAN row with no decisions → AUTO_RESOLVED
 * 2. Any CONFLICT cell without a decision → PENDING
 * 3. Otherwise, classify by the decisions present:
 *    - All MINE → CHOSE_MINE
 *    - All THEIRS → CHOSE_THEIRS
 *    - Mix or CUSTOM/CLEAR → CHOSE_MIXED
 *
 * A custom value on a cell that is not a CONFLICT counts as MINE: there is no
 * conflict to resolve, so the edit just becomes Mine's value.
 */
export function rowDecisionStatus(
  row: MergeRowDto,
  decisions: Record<string, MergeDecisionDto>
): RowDecisionStatus {
  // Collect all decision keys for this row
  const rowDecisionKeys = row.Periods.map((period) => periodKey(row, period));
  const rowDecisions = rowDecisionKeys.filter((key) => key in decisions);

  // Rule 1: CLEAN row with no decisions → AUTO_RESOLVED
  if (row.RowStatus === 'CLEAN' && rowDecisions.length === 0) {
    return 'AUTO_RESOLVED';
  }

  // Rule 2: Any CONFLICT cell without a decision → PENDING
  for (const period of row.Periods) {
    if (period.Class === 'CONFLICT') {
      const key = periodKey(row, period);
      if (!(key in decisions)) {
        return 'PENDING';
      }
    }
  }

  // Rule 3: Classify by decisions
  const decidedChoices = row.Periods.filter((period) =>
    rowDecisions.includes(periodKey(row, period))
  ).map((period) => {
    const choice = decisions[periodKey(row, period)].choice;
    return choice === 'CUSTOM' && period.Class !== 'CONFLICT' ? 'MINE' : choice;
  });

  // No decisions found (edge case; shouldn't normally reach here)
  if (decidedChoices.length === 0) {
    return 'PENDING';
  }

  // All decisions are MINE
  const allMine = decidedChoices.every((choice) => choice === 'MINE');
  if (allMine) {
    return 'CHOSE_MINE';
  }

  // All decisions are THEIRS
  const allTheirs = decidedChoices.every((choice) => choice === 'THEIRS');
  if (allTheirs) {
    return 'CHOSE_THEIRS';
  }

  // Mix of decisions or CUSTOM/CLEAR present
  return 'CHOSE_MIXED';
}

/**
 * Map a RowDecisionStatus to its display label.
 */
export function statusLabel(status: RowDecisionStatus): string {
  const labels: Record<RowDecisionStatus, string> = {
    PENDING: 'Decision Pending',
    CHOSE_MINE: 'Chose Mine',
    CHOSE_THEIRS: 'Chose Theirs',
    CHOSE_MIXED: 'Chose Mixed',
    AUTO_RESOLVED: 'Auto Resolved',
  };
  return labels[status];
}
